import { api } from './axios';

/**
 * Annual Tournaments API — ежегодные турниры
 * Базовый путь: /api/annual-tournament
 * @see api_docs/api_annual_tournament_reference.md
 */

// ==================== Основные endpoints ====================

/**
 * Получить список всех ежегодных турниров
 * GET /api/annual-tournament
 * @returns {Promise<Array>} Список ежегодных турниров по годам
 */
export const getAnnualTournaments = () => {
    return api.get('/annual-tournament').then((res) => res.data);
};

/**
 * Получить текущий активный ежегодный турнир
 * GET /api/annual-tournament/current
 * @returns {Promise<Object|null>} Текущий ежегодный турнир или null (404)
 */
export const getCurrentAnnualTournament = () => {
    return api.get('/annual-tournament/current')
        .then((res) => res.data)
        .catch((err) => {
            if (err.response?.status === 404) {
                console.log('[getCurrentAnnualTournament] Нет активного турнира');
                return null;
            }
            console.error('[getCurrentAnnualTournament] Ошибка:', err.message);
            throw err;
        });
};

/**
 * Получить ежегодный турнир по году
 * GET /api/annual-tournament/{year}
 * @param {number} year - Год турнира
 * @returns {Promise<Object>} Данные турнира
 */
export const getAnnualTournamentByYear = (year) => {
    if (!year) {
        throw new Error('Year is required');
    }
    return api.get(`/annual-tournament/${year}`).then((res) => res.data);
};

/**
 * Получить ежегодный турнир по ID
 * GET /api/annual-tournament/id/{id}
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Данные турнира
 */
export const getAnnualTournamentById = (id) => {
    if (!id) {
        throw new Error('Tournament ID is required');
    }
    return api.get(`/annual-tournament/${id}`).then((res) => res.data);
};

/**
 * Получить победителей ежегодных турниров по годам
 * GET /api/tournaments/annual/winners
 * ⚠️ Endpoint может быть не реализован на сервере
 * @returns {Promise<Array>} Список победителей по годам
 */
export const getAnnualWinners = () => {
    return api.get('/tournaments/annual/winners')
        .then((res) => res.data)
        .catch((err) => {
            console.warn('[getAnnualWinners] Endpoint не доступен, используем fallback');
            // Fallback: получаем все турниры и фильтруем завершённые
            return getAnnualTournaments().then((tournaments) =>
                tournaments
                    .filter(t => t.status === 'COMPLETED' && t.winnerName)
                    .map(t => ({
                        year: t.year,
                        tournamentId: t.id,
                        winnerId: t.winnerId,
                        winnerName: t.winnerName,
                        winnerAvatar: null,
                        runnerUpId: null,
                        runnerUpName: null,
                        runnerUpAvatar: null,
                        finalScore: null,
                        prizeAmount: t.prizePoolAmount,
                        currency: t.currency
                    }))
            );
        });
};

/**
 * Получить статистику ежегодного турнира за год
 * GET /api/annual-tournament/{year}/stats
 * ⚠️ Endpoint может быть не реализован на сервере
 * @param {number} year - Год турнира
 * @returns {Promise<Object>} Статистика турнира
 */
export const getAnnualTournamentStats = (year) => {
    if (!year) {
        throw new Error('Year is required');
    }
    return api.get(`/annual-tournament/${year}/stats`)
        .then((res) => res.data)
        .catch((err) => {
            console.warn(`[getAnnualTournamentStats] Endpoint /${year}/stats не доступен`);
            // Fallback: получаем турнир и возвращаем базовую статистику
            return getAnnualTournamentByYear(year).then((tournament) => ({
                year,
                tournamentId: tournament.id,
                totalParticipants: tournament.participantCount,
                totalMatches: 0,
                totalRounds: 0,
                totalTracks: 0,
                totalVotes: 0,
                averageVotesPerMatch: 0,
                duration: {
                    startDate: tournament.startDate,
                    endDate: tournament.endDate,
                    days: tournament.endDate && tournament.startDate
                        ? Math.ceil((new Date(tournament.endDate) - new Date(tournament.startDate)) / (1000 * 60 * 60 * 24))
                        : 0
                },
                topParticipants: [],
                mostVotedMatch: null
            }));
        });
};

/**
 * Получить лидерборд ежегодного турнира
 * GET /api/annual-tournament/{year}/leaderboard
 * @param {number} year - Год турнира
 * @param {Object} [options] - Опции запроса
 * @param {number} [options.page=0] - Номер страницы
 * @param {number} [options.size=20] - Размер страницы
 * @returns {Promise<Object>} Лидерборд с пагинацией
 */
export const getAnnualTournamentLeaderboard = (year, options = {}) => {
    if (!year) {
        throw new Error('Year is required');
    }
    const { page = 0, size = 20 } = options;
    return api.get(`/annual-tournament/${year}/leaderboard`, {
        params: { page, size }
    }).then((res) => res.data);
};

/**
 * Зарегистрироваться на ежегодный турнир
 * POST /api/annual-tournament/{year}/register
 * @param {number} year - Год турнира
 * @param {Object} [data] - Данные регистрации
 * @param {string|null} [data.teamName] - Название команды (null для одиночного участия)
 * @returns {Promise<Object>} Данные участника {tournamentId, participantId, seed}
 */
export const registerForAnnualTournament = (year, data = {}) => {
    if (!year) {
        throw new Error('Year is required');
    }
    return api.post(`/annual-tournament/${year}/register`, data).then((res) => res.data);
};

/**
 * Отменить регистрацию на ежегодный турнир
 * POST /api/annual-tournament/{year}/unregister
 * @param {number} year - Год турнира
 * @returns {Promise<void>}
 */
export const unregisterFromAnnualTournament = (year) => {
    if (!year) {
        throw new Error('Year is required');
    }
    return api.post(`/annual-tournament/${year}/unregister`).then((res) => res.data);
};

/**
 * Проверить статус регистрации пользователя на турнир
 * GET /api/annual-tournament/{year}/registration-status
 * @param {number} year - Год турнира
 * @returns {Promise<Object>} Статус регистрации {isRegistered, participantId, registeredAt}
 */
export const getRegistrationStatus = (year) => {
    if (!year) {
        throw new Error('Year is required');
    }
    return api.get(`/annual-tournament/${year}/registration-status`).then((res) => res.data);
};

/**
 * Получить участников ежегодного турнира
 * GET /api/annual-tournament/{year}/participants
 * @param {number} year - Год турнира
 * @param {Object} [options] - Опции запроса
 * @param {number} [options.page=0] - Номер страницы
 * @param {number} [options.size=50] - Размер страницы
 * @returns {Promise<Object>} Список участников с пагинацией
 */
export const getAnnualTournamentParticipants = (year, options = {}) => {
    if (!year) {
        throw new Error('Year is required');
    }
    const { page = 0, size = 50 } = options;
    return api.get(`/annual-tournament/${year}/participants`, {
        params: { page, size }
    }).then((res) => res.data);
};

// ==================== Управление (ADMIN) ====================

/**
 * Создать ежегодный турнир (ADMIN)
 * POST /api/annual-tournament
 * @param {Object} data - Данные турнира
 * @param {number} data.year - Год турнира
 * @param {string} data.title - Название турнира
 * @param {string} data.description - Описание
 * @param {string} [data.coverImageId] - ID обложки
 * @param {string} [data.prizePool] - Тип призового фонда: CASH, POINTS, MIXED
 * @param {number} [data.prizePoolAmount] - Сумма призового фонда
 * @param {string} [data.currency] - Валюта (RUB, USD, EUR)
 * @param {number} data.maxParticipants - Максимум участников
 * @param {string} data.registrationStart - Начало регистрации (ISO 8601)
 * @param {string} data.registrationEnd - Конец регистрации (ISO 8601)
 * @param {string} data.startDate - Дата начала (ISO 8601)
 * @param {string} data.endDate - Дата окончания (ISO 8601)
 * @param {string} [data.rules] - Правила
 * @param {Array} [data.sponsors] - Список спонсоров
 * @returns {Promise<Object>} Созданный турнир
 */
export const createAnnualTournament = (data) => {
    return api.post('/annual-tournament', data).then((res) => res.data);
};

/**
 * Обновить ежегодный турнир (ADMIN)
 * PUT /api/annual-tournament/{id}
 * @param {string} id - ID турнира
 * @param {Object} data - Данные для обновления
 * @returns {Promise<Object>} Обновлённый турнир
 */
export const updateAnnualTournament = (id, data) => {
    return api.put(`/annual-tournament/${id}`, data).then((res) => res.data);
};

/**
 * Удалить ежегодный турнир (ADMIN)
 * DELETE /api/annual-tournament/{id}
 * @param {string} id - ID турнира
 * @returns {Promise<void>}
 */
export const deleteAnnualTournament = (id) => {
    return api.delete(`/annual-tournament/${id}`).then((res) => res.data);
};

/**
 * Начать ежегодный турнир (ADMIN)
 * POST /api/annual-tournament/{id}/start
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Турнир со статусом ACTIVE
 */
export const startAnnualTournament = (id) => {
    return api.post(`/annual-tournament/${id}/start`).then((res) => res.data);
};

/**
 * Завершить ежегодный турнир (ADMIN)
 * POST /api/annual-tournament/{id}/finish
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Турнир со статусом COMPLETED и winnerId
 */
export const finishAnnualTournament = (id) => {
    return api.post(`/annual-tournament/${id}/finish`).then((res) => res.data);
};

/**
 * Отменить ежегодный турнир (ADMIN)
 * POST /api/annual-tournament/{id}/cancel
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Турнир со статусом CANCELLED
 */
export const cancelAnnualTournament = (id) => {
    return api.post(`/annual-tournament/${id}/cancel`).then((res) => res.data);
};

// ==================== Export ====================

export default {
    getAnnualTournaments,
    getCurrentAnnualTournament,
    getAnnualTournamentByYear,
    getAnnualTournamentById,
    getAnnualWinners,
    getAnnualTournamentStats,
    getAnnualTournamentLeaderboard,
    getRegistrationStatus,
    getAnnualTournamentParticipants,
    registerForAnnualTournament,
    unregisterFromAnnualTournament,
    createAnnualTournament,
    updateAnnualTournament,
    deleteAnnualTournament,
    startAnnualTournament,
    finishAnnualTournament,
    cancelAnnualTournament
};
